// src/hooks/useOrgAreaConfig.js
//
// Pass 82E — load an organization's operating-area / service-location / meeting-point config (read-only) and build the
// resolver context the area filter + reads share. Fail-soft: a load error yields empty config (an unconfigured org
// simply has no areas — a graceful state, never a crash). Refreshable so the Settings UI can re-read after a write.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { loadOrgAreaConfig, buildAreaResolverContext } from '../utils/orgAreaConfig';

export function useOrgAreaConfig(organizationId, organization = null) {
  const [config, setConfig] = useState({ areas: [], serviceLocations: [], meetingPoints: [] });
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    if (!organizationId) { setConfig({ areas: [], serviceLocations: [], meetingPoints: [] }); return undefined; }
    setLoading(true);
    (async () => {
      try {
        const c = await loadOrgAreaConfig(db, organizationId);
        if (alive) setConfig(c);
      } catch (e) { if (alive) setConfig({ areas: [], serviceLocations: [], meetingPoints: [] }); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [organizationId, nonce]);

  const resolverCtx = useMemo(() => buildAreaResolverContext(organization || {}, config.areas), [organization, config.areas]);
  return { ...config, loading, refresh, resolverCtx };
}
